import {isAxiosError} from 'axios'
import i18next from 'i18next'
import {toast} from '@utilities/toast/toast.tsx'
import {forwardRef, ReactNode, Ref, RefAttributes} from 'react'
import {UseInfiniteQueryResult} from '@tanstack/react-query'
import {
    DateRange,
    DateRangeLabels,
    ErrorResponseData,
    MaritalStatus,
    PatientAgeRange,
    TableFiltersPayload,
    WorkSector,
    TableFiltersValidationSchema,
    TableOrder
} from '@/types.ts'
import dayjs from 'dayjs'
import {FieldNamesMarkedBoolean} from 'react-hook-form'
import {TKey} from '@/translations/i18n.tsx'
import {z} from 'zod'
import {VisibilityState} from '@tanstack/react-table'
import {PatientsColumn, PatientEventsColumn} from '@/features/patients/types'
import {ProjectsColumn} from '@/features/projects/types'
import {OperativeUsersColumn, UserEventsColumn, UsersColumn} from '@/features/users/types'
import {TreatmentRequestsColumn} from '@/features/treatment-requests/types'
import {AccessesColumn} from '@/features/accesses/types'
import it from '@/translations/it/it.json'
export const capitalize = (text: string): string => {
    return text
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.toLowerCase().slice(1))
        .join(' ')
}

export const megabytesToBytes = (megabytes: number) => megabytes * 1_000_000

/**
 * Function that throws an error, it's useful to prevent impossible states in the application.
 * Example: const id = params.id ?? raise('No id provided'), in this case id is a string instead of string | undefined
 * @param error
 */
export const raise = (error: string): never => {
    throw new Error(error)
}

export const debounce = <T extends (...args: Parameters<T>) => ReturnType<T>>(
    callback: T,
    delay: number
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout>

    return (...args: Parameters<T>) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            callback(...args)
        }, delay)
    }
}

/**
 * Maps a BE error to its `errors:<code>` label when the code has a translation, falling back to the
 * generic one. Use it when the error is rendered inline; `errorHandler` covers the toast case.
 */
export const resolveErrorLabel = (error: unknown): TKey =>
    isAxiosError<ErrorResponseData>(error) &&
    error.response?.data.code &&
    error.status != 500 &&
    error.response.data.code in it.errors
        ? (`errors:${error.response.data.code}` as TKey)
        : 'errors:default'

export const errorHandler = (error: unknown) => toast.error(i18next.t(resolveErrorLabel(error)))

const RESOURCE_FORBIDDEN_ERROR_LABELS: Record<string, TKey> = {
    'project-forbidden': 'errors:project-forbidden',
    'patient-forbidden': 'errors:patient-forbidden',
    'treatment-request-forbidden': 'errors:treatment-request-forbidden'
}

/**
 * The BE answers the `show` endpoints of projects, patients and treatment requests with a 403 carrying a
 * resource-scoped code when the user is not in the resource's team. Returns `null` for any other error, so
 * callers keep their own default handling.
 */
export const resolveResourceForbiddenErrorLabel = (error: unknown): TKey | null => {
    if (!isAxiosError<ErrorResponseData>(error) || error.response?.status != 403) {
        return null
    }
    const code = error.response.data?.code
    return (code ? RESOURCE_FORBIDDEN_ERROR_LABELS[code] : undefined) ?? null
}

export const ObjectKeys = Object.keys as <T extends object>(object: T) => Array<keyof T>
export const ObjectValues = Object.values as <T extends object>(object: T) => Array<T[keyof T]>
export const ObjectEntries = Object.entries as <T extends object>(
    object: T
) => Array<{[K in keyof T]: [K, T[K]]}[keyof T]>

export function genericForwardRef<TRef, TProps = object>(
    render: (props: TProps, ref: Ref<TRef>) => ReactNode
): (props: TProps & RefAttributes<TRef>) => ReactNode {
    return forwardRef(render) as (props: TProps & RefAttributes<TRef>) => ReactNode
}

export const downloadFile = (file: File) => {
    const fileLocalURL = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = fileLocalURL
    link.download = file.name
    link.click()
    URL.revokeObjectURL(fileLocalURL)
}

export const buildFile = ({
    extension,
    fileName,
    data
}: {
    extension: 'xlsx' | 'xls' | 'csv' | 'zip' | 'pdf' | 'txt'
    fileName: string
    data: Blob
}) => {
    const type = {
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        xls: 'application/vnd.ms-excel',
        csv: 'text/csv',
        zip: 'application/zip',
        pdf: 'application/pdf',
        txt: 'text/plain'
    }[extension]
    return new File([data], `${fileName}.${extension}`, {type})
}

export const infiniteQueryFetchNextPage = async (infiniteQuery: UseInfiniteQueryResult) => {
    if (infiniteQuery.hasNextPage && !infiniteQuery.isFetching) {
        await infiniteQuery.fetchNextPage()
    }
}

export const pickPropsFromObj = <T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
    return keys.reduce(
        (acc, key) => {
            acc[key] = obj[key]
            return acc
        },
        {} as Pick<T, K>
    )
}

export const excludePropsFromObj = <T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
    return Object.entries(obj).reduce(
        (acc, [key, value]) => {
            if (!keys.includes(key as K)) {
                acc[key as Exclude<keyof T, K>] = value
            }
            return acc
        },
        {} as Omit<T, K>
    )
}

export const getPatientServiceType = (
    birthDate: Date
): {serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>; years: number; months: number} => {
    const years = dayjs().diff(birthDate, 'years')
    const months = dayjs().diff(birthDate, 'month')
    return {
        serviceType: years > 17 ? 'adults' : 'evolutionary_age',
        years,
        months
    }
}

export const isInCurrentCalendarYear = (date?: Date | string | null) => {
    if (!date || !dayjs(new Date(date)).isValid()) return false

    const providedDate = dayjs(new Date(date))
    const startOfYear = dayjs().startOf('year')
    const endOfYear = dayjs().endOf('year')

    return providedDate.isAfter(startOfYear) && providedDate.isBefore(endOfYear)
}

export const getDateFromLabel = (dateLabel: DateRangeLabels): DateRange => {
    const periods: {[key: string]: DateRange} = {
        today: {
            startDate: dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            endDate: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
        },
        yesterday: {
            startDate: dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            endDate: dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DD HH:mm:ss')
        },
        last_week: {
            startDate: dayjs().subtract(7, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            endDate: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
        },
        last_month: {
            startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD HH:mm:ss'),
            endDate: dayjs().format('YYYY-MM-DD HH:mm:ss')
        },
        last_3_months: {
            startDate: dayjs().subtract(3, 'months').format('YYYY-MM-DD HH:mm:ss'),
            endDate: dayjs().format('YYYY-MM-DD HH:mm:ss')
        },
        last_6_months: {
            startDate: dayjs().subtract(6, 'months').format('YYYY-MM-DD HH:mm:ss'),
            endDate: dayjs().format('YYYY-MM-DD HH:mm:ss')
        },
        last_year: {
            startDate: dayjs().startOf('year').format('YYYY-MM-DD HH:mm:ss'),
            endDate: dayjs().endOf('year').format('YYYY-MM-DD HH:mm:ss')
        }
    }
    return periods[dateLabel]
}

// Maps
export const workServiceToLabel = {
    adults: 'commons:adults',
    evolutionary_age: 'commons:evolutionary_age',
    evolutionary_age_adults: 'commons:evolutionary_age_adults'
} as const satisfies Record<PatientAgeRange, TKey>

export const maritalStatusesToLabel = {
    single: 'marital_status:single',
    married: 'marital_status:married',
    separated: 'marital_status:separated',
    divorced: 'marital_status:divorced',
    widowed: 'marital_status:widowed',
    civil_union: 'marital_status:civil_union',
    former_civil_union_deceased_partner: 'marital_status:former_civil_union_deceased_partner',
    former_civil_union_dissolution: 'marital_status:former_civil_union_dissolution',
    not_recorded: 'marital_status:not_recorded'
} as const satisfies Record<NonNullable<MaritalStatus>, TKey>

export const workSectorToLabel = {
    outpatient: 'commons:outpatient',
    domiciliary: 'commons:domiciliary',
    outpatient_domiciliary: 'users:outpatient_domiciliary'
} as const satisfies Record<WorkSector, TKey>

export const patientServiceTypeMapToLabel = {
    adults: 'commons:adults',
    evolutionary_age: 'commons:evolutionary_age'
} as const satisfies Record<Exclude<PatientAgeRange, 'evolutionary_age_adults'>, TKey>

export const getIsRowSelected = <T extends {id: string}>({
    dataItem,
    isSelectAll,
    selectedIds,
    unselectedIds
}: {
    dataItem?: T
    isSelectAll: boolean
    selectedIds: string[]
    unselectedIds: string[]
}) => (dataItem ? (isSelectAll && !unselectedIds.includes(dataItem.id)) || selectedIds.includes(dataItem.id) : false)

export const getDirtyFieldsFromFormValues = <Schema extends Record<string, unknown>>(
    dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<Schema>>>,
    formValues: Schema,
    exceptions: Array<keyof Schema> = []
): Partial<Schema> => {
    if (typeof dirtyFields !== 'object' || dirtyFields == null || !formValues) {
        return {}
    }

    const dirtyFieldsEntries = Object.entries(dirtyFields)

    // Filter out exceptions from dirtyFieldsEntries
    const filteredDirtyFieldsEntries = dirtyFieldsEntries.filter(([key]) => !exceptions.includes(key))

    // Process the filtered dirty fields
    return filteredDirtyFieldsEntries.reduce<Partial<Schema>>((acc, [key, isDirty]) => {
        if (isDirty && key in formValues) {
            return {
                ...acc,
                [key]: formValues[key]
            }
        }
        return acc
    }, {})
}

export const REPORT_TEXTAREA_LIMIT = 2500

export const filtersParamToPayload = (filtersParam?: string): TableFiltersPayload | undefined => {
    if (!filtersParam) {
        return undefined
    }

    const filters = TableFiltersValidationSchema.parse(JSON.parse(filtersParam))

    return {
        filters: {
            ...filters,
            fields: filters.fields.map(field => {
                let remappedField: NonNullable<TableFiltersPayload['filters']>['fields'][number] = field
                if (remappedField.key == 'outRegion') {
                    remappedField = {
                        key: 'residenceAslDistrictId',
                        operator: remappedField.value == 'yes' ? 'is' : 'is not',
                        value: null
                    }
                }
                if (remappedField.operator == 'empty') {
                    if (
                        remappedField.key == 'absence_20' ||
                        remappedField.key == 'absence_50' ||
                        remappedField.key == 'absence_nr' ||
                        remappedField.key == 'access_100'
                    ) {
                        remappedField = {...remappedField, operator: '!=', value: remappedField.key}
                    } else {
                        remappedField = {...remappedField, operator: 'is', value: null}
                    }
                }
                if (remappedField.operator == 'not empty') {
                    if (
                        remappedField.key == 'absence_20' ||
                        remappedField.key == 'absence_50' ||
                        remappedField.key == 'absence_nr' ||
                        remappedField.key == 'access_100'
                    ) {
                        remappedField = {...remappedField, operator: '=', value: remappedField.key}
                    } else {
                        remappedField = {...remappedField, operator: 'is not', value: null}
                    }
                }
                if (remappedField.operator == 'like') {
                    remappedField = {...remappedField, value: `%${remappedField.value}%`}
                }
                return remappedField
            })
        }
    }
}

export const sortParamToPayload = (sortParam?: string): TableOrder[] | undefined => {
    if (!sortParam) {
        return undefined
    }
    return z.array(TableOrder).parse(JSON.parse(sortParam))
}

export const columnVisibilitySearchParamsToPayload = (
    columnParser:
        | typeof PatientsColumn
        | typeof ProjectsColumn
        | typeof UsersColumn
        | typeof OperativeUsersColumn
        | typeof TreatmentRequestsColumn
        | typeof AccessesColumn
        | typeof PatientEventsColumn
        | typeof UserEventsColumn,
    columnVisibility?: string
): VisibilityState | undefined => {
    if (!columnVisibility) return undefined

    try {
        return z.record(columnParser, z.boolean()).parse(JSON.parse(columnVisibility))
    } catch {
        return undefined
    }
}

export const columnVisibilityToSearchParams = (columnVisibility: VisibilityState): string => {
    return JSON.stringify(columnVisibility)
}

/**
 * Formats a number as currency with locale support
 * @param amount - The number to format
 * @param currency - The currency code (default: 'EUR')
 * @param locale - The locale for formatting (default: 'it-IT')
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted currency string
 */
export const formatCurrency = (
    amount: number,
    currency: string = 'EUR',
    locale: string = 'it-IT',
    options?: Partial<Intl.NumberFormatOptions>
): string => {
    const defaultOptions: Intl.NumberFormatOptions = {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options
    }

    return new Intl.NumberFormat(locale, defaultOptions).format(amount)
}

/**
 * Formats a number as percentage with locale support
 * @param amount - The number to format (e.g., 0.1234 becomes 12.3%)
 * @param locale - The locale for formatting (default: 'it-IT')
 * @param options - Additional Intl.NumberFormat options
 * @param minimumFractionDigits - The minimum number of decimal places (default: 0)
 * @param maximumFractionDigits - The maximum number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
    amount: number,
    locale: string = 'it-IT',
    minimumFractionDigits: number = 0,
    maximumFractionDigits: number = 1,
    options?: Partial<Intl.NumberFormatOptions>
): string => {
    const defaultOptions: Intl.NumberFormatOptions = {
        style: 'percent',
        minimumFractionDigits,
        maximumFractionDigits,
        ...options
    }

    return new Intl.NumberFormat(locale, defaultOptions).format(amount)
}

export const formatNumber = (amount: number, options?: Partial<Intl.NumberFormatOptions>): string =>
    new Intl.NumberFormat(i18next.language, {
        ...options
    }).format(amount)
